<template>
  <main class="verify-mnemonic-page">
    <AppNavBar :border="false" background-color="#f5f5f5" />

    <section class="verify-body">
      <h1>{{ t('auth.verifyMnemonicTitle') }}</h1>
      <div class="verify-tip">
        <app-image :src="circleYesIcon" alt="" />
        <span>{{ t('auth.verifyMnemonicTip') }}</span>
      </div>

      <div class="slots">
        <button
          v-for="index in verifyIndexes"
          :key="index"
          type="button"
          class="slot"
          @click="clearSlot(index)"
        >
          <span class="slot-label">{{ ordinalNumber(index) }}</span>
          <div data-strong :class="{ active: activeIndex === index }">{{ verifyMap[index] || '' }}</div>
        </button>
      </div>

      <div class="word-pool">
        <button
          v-for="word in shuffledWords"
          :key="word"
          type="button"
          class="word-chip"
          :class="{ used: usedWords.has(word) }"
          :disabled="usedWords.has(word)"
          @click="pickWord(word)"
        >
          {{ word }}
        </button>
      </div>
    </section>

    <footer class="verify-footer">
      <AuthThemeButton block :disabled="!isComplete" @click="submitVerify">
        {{ isExport ? t('common.confirm') : t('auth.next') }}
      </AuthThemeButton>
    </footer>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useProfileStore } from '@/stores/profile';
import { getRouteState } from '@/utils/route-state';
import { getRecoverableMnemonic } from '@/utils/walletSession';
import circleYesIcon from '@/assets/images/circle_yes_icon.png';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, 'auth-verify-mnemonic', {}));

const isExport = computed(() => String(pageState.value.export || '') === '1');
const mnemonic = computed(() => getRecoverableMnemonic(profileStore.accountStorageAddress));
const words = computed(() => mnemonic.value.split(' ').filter(Boolean));

const verifyIndexes = ref([]);
const verifyMap = ref({});
const activeIndex = ref(-1);
const shuffledWords = ref([]);

function initVerify() {
  const list = words.value;
  if (!list.length) return;

  const indexes = [];
  while (indexes.length < 3) {
    const index = Math.floor(Math.random() * list.length) + 1;
    if (!indexes.includes(index)) indexes.push(index);
  }
  indexes.sort((a, b) => a - b);
  verifyIndexes.value = indexes;
  activeIndex.value = indexes[0];
  shuffledWords.value = [...list].sort(() => Math.random() - 0.5);
}

onMounted(() => {
  if (!mnemonic.value) {
    showFailToast(t('auth.mnemonicUnavailable'));
    return;
  }
  initVerify();
});

const usedWords = computed(() => new Set(Object.values(verifyMap.value)));
const isComplete = computed(() => verifyIndexes.value.every((index) => verifyMap.value[index]));

function ordinalNumber(index) {
  return t('auth.ordinalNumber', { number: index });
}

function clearSlot(index) {
  const next = { ...verifyMap.value };
  delete next[index];
  verifyMap.value = next;
  activeIndex.value = verifyIndexes.value.find((item) => !verifyMap.value[item]) ?? index;
}

function pickWord(word) {
  if (usedWords.value.has(word)) return;

  const target = verifyIndexes.value.find((index) => !verifyMap.value[index]);
  if (!target) return;

  verifyMap.value = {
    ...verifyMap.value,
    [target]: word
  };
  activeIndex.value = verifyIndexes.value.find((index) => !verifyMap.value[index]) ?? -1;
}

function submitVerify() {
  const list = words.value;
  const passed = verifyIndexes.value.every((index) => list[index - 1] === verifyMap.value[index]);

  if (!passed) {
    showFailToast(t('auth.mnemonicCheckFailed'));
    return;
  }

  if (isExport.value) {
    profileStore.setAccountBackup(true);
    showPassedToast(t('auth.mnemonicCheckSuccess'));
    router.replace('/home/index');
    return;
  }

  profileStore.setAccountBackup(true);
  router.replace('/auth/wallet-success');
}
</script>

<style scoped lang="scss">
.verify-mnemonic-page {
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  background: #f5f5f5;
}

.verify-body {
  flex: 1;
  padding: 0 14px;

  h1 {
    margin: 0;
    color: #000000;
    font-size: 22px;
    font-weight: 700;
  }
}

.verify-tip {
  display: flex;
  align-items: center;
  margin-top: 21px;

  .app-image {
    width: 14px;
    height: 14px;
    margin-right: 5px;
  }

  span {
    color: #666666;
    font-size: 12px;
  }
}

.slots {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.slot {
  width: 31%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: center;

  .slot-label {
    display: block;
    color: #333333;
    font-size: 16px;
  }

  [data-strong] {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    margin-top: 10px;
    border-radius: 10px;
    background: #ffffff;
    color: #000000;
    font-size: 14px;
    font-weight: 500;

    &.active {
      border: 1px solid #2667ff;
    }
  }
}

.word-pool {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 20px;
  padding: 20px 14px 10px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.word-chip {
  width: 48%;
  height: 40px;
  margin-bottom: 10px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f6f6f6;
  color: #000000;
  font-size: 14px;

  &.used {
    border-color: #999999;
    background: rgba(135, 134, 134, 0.15);
    color: #666666;
  }
}

.verify-footer {
  padding: 0 14px 30px;
}
</style>
